"use client"
import Link from "next/link"
import Image from "next/image"
import { motion } from "motion/react"
import { useSiteSettings, useSetting, useLinks } from "@/components/site/SiteSettingsProvider"
import { CookieSettingsButton } from "@/components/ui/ConsentBanner"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const BORDER = "rgba(255,255,255,0.08)"

const defaultSocialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/boldpiq/" },
  { label: "Facebook", href: "https://www.facebook.com/boldpiq" },
  { label: "Pinterest", href: "https://za.pinterest.com/boldpiq/" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/boldpiq/" },
  { label: "Google Maps", href: "https://www.google.com/maps/place/BoldPiq/@-33.8896839,18.5047937,17z/data=!3m1!4b1!4m6!3m5!1s0x1dcc5d74a5e42ecd:0x5d856bbf2cc68960!8m2!3d-33.8896839!4d18.5073686!16s%2Fg%2F11ykd7gz7t" },
]

const defaultLegalLinks = [
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Refund Policy", href: "/refund" },
  { label: "Cookie Policy", href: "/cookie-policy" },
  { label: "PAIA Manual", href: "/paia" },
  { label: "DPA", href: "/dpa" },
  { label: "Disclaimers", href: "/disclaimers" },
  { label: "AI Transparency", href: "/ai-transparency" },
  { label: "Accessibility", href: "/accessibility" },
]

const defaultFooterNav = [
  { label: "Work", href: "/work" },
  { label: "Services", href: "/services" },
  { label: "For Contractors", href: "/contractors" },
  { label: "Offers", href: "/offers" },
  { label: "Instant Estimate", href: "/estimate" },
  { label: "Reviews", href: "/review" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Book a Call", href: "/contact" },
]

export function Footer() {
  // Every value below falls back to the copy that shipped with the build, so the
  // footer is never blank if the CMS is unreachable or a field was cleared.
  const s = useSiteSettings()
  const socialLinks = useLinks("socialLinks", defaultSocialLinks)
  const legalLinks = useLinks("footerLegalLinks", defaultLegalLinks)
  const footerNav = useLinks("footerNavItems", defaultFooterNav)
  const tagline = useSetting("footerTagline", "Strategically crafted websites designed to support growth.")
  const phone = useSetting("phone", "+27 76 174 1248")
  const phoneLink = useSetting("phoneLink", "+27761741248")
  const emailPrimary = useSetting("emailPrimary", "team@boldpiq.com")
  const emailSupport = useSetting("emailSupport", "support@boldpiq.com")
  const whatsappLabel = useSetting("whatsappLabel", "WhatsApp \u2014 fastest, anywhere")
  const whatsappHref = useSetting(
    "whatsappHref",
    "https://api.whatsapp.com/send/?phone=27761741248&text&type=phone_number&app_absent=0",
  )
  const marketsNote = useSetting("footerMarketsNote", "Working with businesses across the \ud83c\uddec\ud83c\udde7 UK & \ud83c\uddff\ud83c\udde6 South Africa.")
  const paymentNote = useSetting(
    "footerPaymentNote",
    "Payment by bank transfer (EFT). UK clients receive a Wise payment link, in Pounds. Card payments via Paystack are being added.",
  )
  const ctaLabel = useSetting("footerCtaLabel", "Start a Project")
  const ctaHref = useSetting("footerCtaHref", "/contact")
  void s

  return (
    <footer style={{ borderTop: `1px solid ${BORDER}`, background: BG, position: "relative", overflow: "hidden" }}>
      {/* Subtle accent glow */}
      <motion.div
        animate={{ opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{ position: "absolute", inset: 0, pointerEvents: "none", background: `radial-gradient(ellipse 60% 50% at 20% 50%, rgba(196,84,26,0.14) 0%, transparent 65%)` }}
      />
      {/* Floating dots */}
      {[
        { x: "5%",  y: "30%", size: 3, delay: 0,   dur: 7  },
        { x: "92%", y: "25%", size: 4, delay: 2,   dur: 9  },
        { x: "75%", y: "70%", size: 3, delay: 1.2, dur: 8  },
        { x: "18%", y: "75%", size: 3, delay: 3.5, dur: 6  },
      ].map((d, i) => (
        <motion.div
          key={i}
          animate={{ y: [0, -16, 0], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: d.dur, repeat: Infinity, ease: "easeInOut", delay: d.delay }}
          style={{ position: "absolute", left: d.x, top: d.y, width: d.size, height: d.size, borderRadius: "50%", background: "rgba(196,84,26,0.7)", pointerEvents: "none" }}
        />
      ))}
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "60px clamp(20px, 4vw, 48px) 32px",
          position: "relative",
        }}
      >
        {/* Top row */}
        <div
          className="flex flex-col md:flex-row md:items-start md:justify-between"
          style={{ gap: 48, marginBottom: 48 }}
        >
          {/* Brand */}
          <div>
            <Link href="/" style={{ display: "inline-block", marginBottom: 12 }}>
              <Image src="/logo.svg" alt="BoldPiq" width={81} height={120} style={{ height: 120, width: "auto" }} />
            </Link>
            <p style={{ color: MUTED, fontSize: 13, lineHeight: 1.65, maxWidth: 280 }}>
              {tagline}
            </p>
          </div>

          {/* Nav links */}
          <nav aria-label="Footer navigation">
            <p style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 16 }}>Navigation</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {footerNav.map((item, i) => (
                <Link key={`${item.href}-${i}`} href={item.href} className="footer-nav-link-orange" style={{ color: MUTED, fontSize: 14, fontWeight: 500, textDecoration: "none" }}>
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>

          {/* Social */}
          <div>
            <p style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 16 }}>Connect</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {socialLinks.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="footer-social-link" style={{ color: MUTED, fontSize: 14, textDecoration: "none", transition: "color 0.2s" }}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          {/* Stay in touch — width-capped on purpose. The payment/markets lines are
              long, and without a cap this column stretched to ~950px while the other
              three sat at 150-350px, unbalancing the row and stranding the CTA
              left-aligned inside it instead of at the right edge. */}
          <div style={{ maxWidth: 340 }}>
            <p style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, marginBottom: 16 }}>Stay in Touch</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* The OFFICIAL business line. BoldPiq runs other numbers (cold-calling
                  among them) — only this one belongs on a public, client-facing surface,
                  and it must match the number in the LocalBusiness schema, on /info, and
                  on invoices. Name/address/phone consistency is what search engines use
                  to trust a local business. */}
              <a href={`tel:${phoneLink}`} className="footer-social-link" style={{ color: MUTED, fontSize: 14, textDecoration: "none", transition: "color 0.2s" }}>{phone}</a>
              <a href={`mailto:${emailPrimary}`} className="footer-social-link" style={{ color: MUTED, fontSize: 14, textDecoration: "none", transition: "color 0.2s" }}>{emailPrimary}</a>
              <a href={`mailto:${emailSupport}`} className="footer-social-link" style={{ color: MUTED, fontSize: 14, textDecoration: "none", transition: "color 0.2s" }}>{emailSupport}</a>
              {/* Same official line as the tel: link above — this previously pointed at
                  a cold-calling number, which sent every website visitor to the wrong
                  inbox. Keep these two in step. */}
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="footer-social-link" style={{ color: MUTED, fontSize: 14, textDecoration: "none", transition: "color 0.2s" }}>{whatsappLabel}</a>
            </div>
            <p style={{ fontSize: 12, color: MUTED, marginTop: 16, lineHeight: 1.55 }}>{marketsNote}</p>
            {/* EFT is what is live today. Paystack is stated as FORTHCOMING, never as
                available — the earlier copy claimed it was already processing payments,
                which it is not; it arrives at the 5-client mark. Wise is the sterling
                rail and is a bank transfer, not a card processor. When Paystack goes
                live, change "are being added" to the present tense; revisit again on
                the move to Stripe after the foreign entity. */}
            <p style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.55 }}>
              {paymentNote}
            </p>
            {/* Primary CTA sits in the right-most column so it lands at the end of
                the reading path, after the contact details rather than beside them. */}
            <Link
              href={ctaHref}
              style={{
                display: "inline-block",
                marginTop: 24,
                padding: "12px 28px",
                background: ACCENT,
                color: "#fff",
                borderRadius: 100,
                fontSize: 13,
                fontWeight: 700,
                textDecoration: "none",
                letterSpacing: "-0.01em",
              }}
            >
              {ctaLabel}
            </Link>
          </div>
        </div>

        {/* Bottom row */}
        <div
          style={{
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <div className="flex flex-wrap" style={{ gap: "6px 0", alignItems: "center" }}>
            {legalLinks.map((l, i) => (
              <span key={`${l.href}-${i}`} style={{ display: "inline-flex", alignItems: "center" }}>
                <Link href={l.href} className="footer-nav-link" style={{ color: MUTED, fontSize: 12, textDecoration: "none" }}>
                  {l.label}
                </Link>
                <span style={{ color: MUTED, fontSize: 12, opacity: 0.4, margin: "0 10px" }}>|</span>
              </span>
            ))}
            {/* Reopens the cookie banner's choices (ConsentBanner.tsx). */}
            <CookieSettingsButton className="footer-nav-link" style={{ color: MUTED, fontSize: 12 }} />
          </div>
          <p style={{ color: MUTED, fontSize: 12, opacity: 0.6 }}>
            © {new Date().getFullYear()} BoldPiq. All rights reserved.
          </p>
        </div>
      </div>
      <style>{`.footer-social-link:hover { color: #fff !important; }`}</style>
    </footer>
  )
}
